"use client";

import React, { useEffect, useRef, useState } from "react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.1,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const galleryItems = [{
  id: 1,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1582385/img/crystal_glassware_macro.png",
  alt: "Macro detail of cut crystal glassware catching the light",
  className: "col-span-2 row-span-2"
}, {
  id: 2,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1582385/img/linen_napkin_texture.png",
  alt: "Close up of fine woven linen texture",
  className: "col-span-1 row-span-1"
}, {
  id: 3,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1582385/img/floral_arrangement_detail.png",
  alt: "Delicate petals of a white floral centerpiece",
  className: "col-span-1 row-span-2"
}, {
  id: 4,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1582385/img/silverware_handle_engraving.png",
  alt: "Engraved detail on polished silverware",
  className: "col-span-1 row-span-1"
}, {
  id: 5,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1582385/img/wax_seal_invitation_close.png",
  alt: "Custom wax seal on a textured invitation envelope",
  className: "col-span-2 row-span-1"
}, {
  id: 6,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1582385/img/candlelight_reflection_table.png",
  alt: "Soft glow of candlelight reflecting on a polished surface",
  className: "col-span-1 row-span-1"
}, {
  id: 7,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1582385/img/champagne_flute_bubbles.png",
  alt: "Effervescent bubbles rising in a crystal champagne flute",
  className: "col-span-1 row-span-1"
}];
export default function SensoryDetails() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05
  });
  return <section id="sensory-details" className="bg-background py-24 lg:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        <div ref={ref} className={`transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <div className="mb-16 md:mb-24 max-w-2xl">
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-foreground tracking-tight mb-6">
              The Art of Nuance
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground font-sans leading-relaxed">
              At Echelon Soiree, we believe true luxury resides in the micro-details. 
              It is the tactile sensation of fine linen, the precise cut of crystal, 
              and the deliberate placement of every element that curates an unforgettable atmosphere.
            </p>
          </div>

          {}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 grid-flow-dense auto-rows-[160px] md:auto-rows-[220px] lg:auto-rows-[280px]">
            {galleryItems.map((item, i) => <div key={item.id} data-index={i} className={`group relative overflow-hidden bg-muted ${item.className}`}>
                <img src={item.src} alt={item.alt} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" loading={i > 3 ? "lazy" : "eager"} />
                {}
                <div className="absolute inset-0 bg-foreground/0 transition-colors duration-700 group-hover:bg-foreground/5 pointer-events-none" />
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}